import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMyResellerOrders, type ResellerOrderRow, type ResellerOrderStatus } from '@/hooks/useResellerOrders';
import { OrderStatusBadge, formatMoney } from '@/components/reseller/OrderStatusBadge';
import { PlusCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function ResellerOrdersPage() {
  const { data: orders, isLoading } = useMyResellerOrders();
  const [status, setStatus] = useState<ResellerOrderStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ResellerOrderRow | null>(null);

  const filtered = useMemo(() => {
    return (orders ?? []).filter((o) => {
      if (status !== 'all' && o.status !== status) return false;
      if (search && !`${o.customer_email} ${o.customer_name ?? ''} ${o.order_number}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [orders, status, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold">Orders</h2>
        <Button asChild><Link to="/reseller/orders/new"><PlusCircle className="mr-2 h-4 w-4" />New Order</Link></Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-2 flex-wrap">
          <Input placeholder="Search order, customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
          <Select value={status} onValueChange={(v) => setStatus(v as ResellerOrderStatus | 'all')}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="activated">Activated</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="hold">On Hold</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground">No orders match your filters.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer" onClick={() => setSelected(o)}>
                    <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                    <TableCell>
                      <div className="font-medium">{o.customer_name ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{o.customer_email}</div>
                    </TableCell>
                    <TableCell>{formatMoney(Number(o.amount), o.currency)}</TableCell>
                    <TableCell><OrderStatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-muted-foreground text-sm">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Order {selected?.order_number}</SheetTitle></SheetHeader>
          {selected && (
            <div className="mt-4 space-y-3 text-sm">
              <Row label="Status"><OrderStatusBadge status={selected.status} /></Row>
              <Row label="Customer">{selected.customer_name ?? '—'} <div className="text-muted-foreground">{selected.customer_email}</div></Row>
              {selected.business_name && <Row label="Business">{selected.business_name}</Row>}
              {selected.contact_phone && <Row label="Phone">{selected.contact_phone}</Row>}
              {selected.domain && <Row label="Domain">{selected.domain}</Row>}
              <Row label="Amount">{formatMoney(Number(selected.amount), selected.currency)}</Row>
              <Row label="Created">{new Date(selected.created_at).toLocaleString()}</Row>
              {selected.activated_at && <Row label="Activated">{new Date(selected.activated_at).toLocaleString()}</Row>}
              {selected.license_id && (
                <LicenseView licenseId={selected.license_id} />
              )}
              {selected.notes && <Row label="Notes"><div className="whitespace-pre-wrap">{selected.notes}</div></Row>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

function LicenseView({ licenseId }: { licenseId: string }) {
  // Simple inline view — the license key is on the licenses page too.
  return (
    <Row label="License">
      <Link to="/reseller/licenses" className="underline text-primary">View in Licenses ({licenseId.slice(0, 8)}…)</Link>
      <button
        className="ml-2 inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
        onClick={() => { navigator.clipboard.writeText(licenseId); toast.success('License ID copied'); }}
      >
        <Copy className="h-3 w-3 mr-1" />Copy ID
      </button>
    </Row>
  );
}
